import {
  Box,
  Button,
  FormField,
  Input,
  InputProps,
  NonCancelableCustomEvent,
  RadioGroup,
  SpaceBetween,
  Textarea,
  Tiles,
} from '@amzn/awsui-components-core';
import React from 'react';
import { ContentEngagementType } from '@amzn/awscat-pattern-library-service-model';
import constants from '../../common/constants';
import {
  validateSalesforceId,
  allowedSalesforceLightningUrls,
  allowdSalesforceUrl,
} from '../../api/ValidationUtil';
import { ContentEngagementUpdateProps } from './ContentEngagementList';
import RadioItem from '../../model/RadioItem';

interface ContentEngagementItemProps {
  engagementIndex: number;
  engagementType: ContentEngagementType;
  notes: string;
  onItemDelete?: (engagementIndex: number) => void;
  onItemUpdate?: (
    engagementIndex: number,
    engagementUpdateProps: ContentEngagementUpdateProps,
  ) => void;
  salesforceId: string;
  salesforceIdError: string;
}

const engagementTypeOptions = [
  RadioItem.create(ContentEngagementType.Project, constants.CONTENT_ENGAGEMENT_TYPE_PROJECT_LABEL),
  RadioItem.create(
    ContentEngagementType.Opportunity,
    constants.CONTENT_ENGAGEMENT_TYPE_OPPORTUNITY_LABEL,
  ),
];

const ContentEngagementItem: React.FunctionComponent<ContentEngagementItemProps> = (
  props: ContentEngagementItemProps,
) => {
  const {
    engagementIndex,
    engagementType,
    notes,
    onItemDelete,
    onItemUpdate,
    salesforceId,
    salesforceIdError,
  } = props;
  const allowedSalesforceUrls = [...allowedSalesforceLightningUrls, ...allowdSalesforceUrl];

  const handleSalesforceIdInput = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    const itemError = checkSalesforceIdError(event.detail.value, engagementType);
    if (onItemUpdate) {
      onItemUpdate(engagementIndex, {
        salesforceId: event.detail.value,
        notes,
        engagementType,
        validationError: itemError,
      });
    }
  };

  const handleNotesInput = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    if (onItemUpdate) {
      onItemUpdate(engagementIndex, {
        salesforceId,
        notes: event.detail.value,
        engagementType,
        validationError: salesforceIdError,
      });
    }
  };

  const handleEngagementTypeChange = (selectedEngagementType: string) => {
    const updatedEngagementType = selectedEngagementType as ContentEngagementType;
    const itemError = checkSalesforceIdError(salesforceId, updatedEngagementType);
    if (onItemUpdate) {
      onItemUpdate(engagementIndex, {
        salesforceId,
        notes,
        engagementType: updatedEngagementType,
        validationError: itemError,
      });
    }
  };

  const clearForm = () => {
    const itemError = checkSalesforceIdError('', ContentEngagementType.Project);
    if (onItemUpdate) {
      onItemUpdate(engagementIndex, {
        salesforceId: '',
        notes: '',
        engagementType: ContentEngagementType.Project,
        validationError: itemError,
      });
    }
  };

  const deleteItem = () => {
    if (onItemDelete) {
      onItemDelete(engagementIndex);
    }
  };

  const checkSalesforceIdError = (
    updatedSalesforceId: string,
    updatedEngagementType: ContentEngagementType,
  ) => {
    let error = '';
    const isOpportunity = updatedEngagementType === ContentEngagementType.Opportunity;
    if (!validateSalesforceId(updatedSalesforceId)) {
      if (!isOpportunity) {
        error = constants.CONTENT_ENGAGEMENT_INVALID_SALESFORCE_PROJECT;
      } else {
        error = constants.CONTENT_ENGAGEMENT_INVALID_SALESFORCE_OPPORTUNITY;
      }
    }
    if (!allowedSalesforceUrls.some((url) => updatedSalesforceId.startsWith(url))) {
      error = constants.CONTENT_ENGAGEMENT_INVALID_SALESFORCE_URL;
    }
    if (!updatedSalesforceId) {
      error = constants.CONTENT_ENGAGEMENT_MISSING_SALESFORCE_PROJECT;
    }
    return error;
  };

  return (
    <div>
      <Box>
        <SpaceBetween direction="vertical" size="xs">
          <FormField
            errorText={salesforceIdError}
            stretch
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Input
              value={salesforceId}
              placeholder={
                allowedSalesforceUrls[0] + constants.CONTENT_ENGAGEMENT_INPUT_SALESFORCE_PLACEHOLDER
              }
              onChange={handleSalesforceIdInput}
              ariaRequired
            />
          </FormField>
          {constants.CONTENT_ENGAGEMENT_RADIO_LABEL}
          <Tiles
            columns={0}
            onChange={({ detail }) => handleEngagementTypeChange(detail.value)}
            value={engagementType}
            items={engagementTypeOptions}
            ariaRequired
          />
          <FormField
            label={constants.CONTENT_ENGAGEMENT_INPUT_NOTES_LABEL}
            stretch
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Textarea
              value={notes}
              placeholder={constants.CONTENT_ENGAGEMENT_INPUT_NOTES_PLACEHOLDER}
              onChange={handleNotesInput}
              rows={3}
              ariaRequired
            />
          </FormField>
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={clearForm} variant="link">
                {constants.CLEAR_BUTTON_TEXT}
              </Button>
              <Button onClick={deleteItem} variant="normal">
                {constants.DELETE_BUTTON_TEXT}
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Box>
    </div>
  );
};

export default ContentEngagementItem;
